import { Card, Stack, Table, Badge, Loader, Center, Tabs, Button } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useResource, useInfiniteResource } from "../../../shared/api";
import {
  PageHeader, EmptyState, ResourceForm, MoneyDisplay, StatusBadge,
  InfiniteTable, formatDateTime, type FormField,
} from "../../../shared/ui";

// Henüz kapanmamış (ödeme bekleyen) paket sipariş durumları. DRAFT/CLOSED/
// CANCELLED hariç — bunlar "operatörün takip etmesi gereken açık paketler".
const PENDING_PACKAGE_STATUSES = "CONFIRMED,PREPARING,READY,SERVED";

/** Sipariş ekranına git + o siparişin drawer'ını aç (Orders.tsx open_id okur). */
function openOrder(orderId: string) {
  window.dispatchEvent(
    new CustomEvent("admin:navigate", {
      detail: { page: "orders", params: { open_id: orderId } },
    }),
  );
}

const COURIER_FIELDS: FormField[] = [
  { name: "name", label: "Ad", required: true },
  { name: "courier_type", label: "Tip (OWN_STAFF / PLATFORM / THIRD_PARTY)", defaultValue: "OWN_STAFF" },
  { name: "phone", label: "Telefon", type: "phone" },
  { name: "vehicle_plate", label: "Plaka" },
  { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
  { name: "is_available", label: "Müsait", type: "switch", defaultValue: true },
];

const STATUS_COLOR: Record<string, string> = {
  ASSIGNED: "yellow",
  PICKED_UP: "blue",
  IN_TRANSIT: "violet",
  DELIVERED: "green",
  FAILED: "red",
  CANCELLED: "gray",
};

export function DeliveryPage() {
  const { data: couriersData, isLoading } = useResource<any>("couriers");
  const { data: assignsData } = useResource<any>("courier-assignments");
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);

  const couriers: any[] = couriersData?.results || couriersData || [];
  const assigns: any[] = assignsData?.results || assignsData || [];

  // Bekleyen Paketler: kapanmamış DELIVERY siparişleri. Kuryesi atanmamış
  // veya ödemesi alınmamış paketler hiçbir ekranda görünmüyordu; burada
  // toplanır, satıra tıklayınca Orders drawer'ından ödeme alınıp kapatılır.
  const {
    items: pendingPackages, total: pendingCount,
    isLoading: pendingLoading, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteResource<any>("orders", {
    filters: {
      fulfillment_type: "DELIVERY",
      kitchen_status: PENDING_PACKAGE_STATUSES,
    },
  });

  if (isLoading) return <Center h={300}><Loader /></Center>;

  return (
    <Stack gap="md">
      <PageHeader
        title="Teslimat / Paket"
        subtitle={`${couriers.length} kurye, ${assigns.length} aktif teslimat`}
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>
            Yeni Kurye
          </Button>
        }
      />
      <Tabs defaultValue="active">
        <Tabs.List>
          <Tabs.Tab value="active">Aktif Teslimatlar</Tabs.Tab>
          <Tabs.Tab
            value="pending"
            rightSection={
              pendingCount > 0 ? (
                <Badge size="sm" circle color="orange" variant="filled">
                  {pendingCount > 99 ? "99+" : pendingCount}
                </Badge>
              ) : undefined
            }
          >
            Bekleyen Paketler
          </Tabs.Tab>
          <Tabs.Tab value="couriers">Kuryeler</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="active" pt="md">
          {assigns.length === 0 ? <EmptyState title="Aktif teslimat yok" /> : (
            <Card padding="sm" radius="md" withBorder>
              <Table.ScrollContainer minWidth={620}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Sipariş</Table.Th>
                    <Table.Th>Kurye</Table.Th>
                    <Table.Th>Atanma</Table.Th>
                    <Table.Th>Tahmini Teslim</Table.Th>
                    <Table.Th>Durum</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {assigns.map((a: any) => (
                    <Table.Tr key={a.id}>
                      <Table.Td>{a.order}</Table.Td>
                      <Table.Td>{a.courier_name}</Table.Td>
                      <Table.Td>{new Date(a.assigned_at).toLocaleString("tr-TR")}</Table.Td>
                      <Table.Td>{a.estimated_delivery_at ? new Date(a.estimated_delivery_at).toLocaleTimeString("tr-TR") : "-"}</Table.Td>
                      <Table.Td><Badge color={STATUS_COLOR[a.status]}>{a.status}</Badge></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </Table.ScrollContainer>
            </Card>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="pending" pt="md">
          <Card padding="sm" radius="md" withBorder>
            <InfiniteTable
              rows={pendingPackages}
              rowKey={(o) => o.id}
              isLoading={pendingLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              onRowClick={(o) => openOrder(o.id)}
              emptyContent={<EmptyState title="Bekleyen paket siparişi yok" />}
              columns={[
                { header: "Sipariş No", render: (o) => <span style={{ fontWeight: 600 }}>{o.order_number || o.id.slice(0, 8)}</span> },
                { header: "Müşteri", render: (o) => o.customer_name || "-" },
                { header: "Tutar", align: "right", render: (o) => <MoneyDisplay minor={o.total_minor} fw={600} /> },
                { header: "Mutfak", render: (o) => <StatusBadge status={o.kitchen_status} /> },
                { header: "Teslimat", render: (o) => o.delivery_status ? <Badge variant="light">{o.delivery_status}</Badge> : <Badge color="gray" variant="light">Başlamadı</Badge> },
                { header: "Tarih", render: (o) => o.confirmed_at ? formatDateTime(o.confirmed_at) : "-" },
              ]}
            />
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="couriers" pt="md">
          {couriers.length === 0 ? <EmptyState title="Kurye yok" /> : (
            <Card padding="sm" radius="md" withBorder>
              <Table.ScrollContainer minWidth={620}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Ad</Table.Th>
                    <Table.Th>Tip</Table.Th>
                    <Table.Th>Telefon</Table.Th>
                    <Table.Th>Plaka</Table.Th>
                    <Table.Th>Müsait</Table.Th>
                    <Table.Th>Aktif</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {couriers.map((c: any) => (
                    <Table.Tr key={c.id}>
                      <Table.Td fw={500}>{c.name}</Table.Td>
                      <Table.Td><Badge variant="light">{c.courier_type}</Badge></Table.Td>
                      <Table.Td>{c.phone || "-"}</Table.Td>
                      <Table.Td>{c.vehicle_plate || "-"}</Table.Td>
                      <Table.Td>{c.is_available ? <Badge color="green">Müsait</Badge> : <Badge color="gray">Meşgul</Badge>}</Table.Td>
                      <Table.Td>{c.is_active ? "✓" : "—"}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </Table.ScrollContainer>
            </Card>
          )}
        </Tabs.Panel>
      </Tabs>

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="couriers"
        title={editing ? "Kurye Düzenle" : "Yeni Kurye"}
        fields={COURIER_FIELDS}
        editId={editing?.id || null}
        initialValues={editing || undefined}
      />
    </Stack>
  );
}
